import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Button, Badge, Alert, Spinner } from 'react-bootstrap';
import { toast } from 'react-toastify';
import AIStats from './components/AIStats';
import LiveAlerts from './components/LiveAlerts';
import DetectionHistory from './components/DetectionHistory';
import CameraManagement from './components/CameraManagement';
import { aiService } from '../../services/aiService';
import './AIMonitoring.css';

interface AIEngineStatus {
  isRunning: boolean;
  uptime: number;
  processedFrames: number;
  activeDetections: number;
}

const AIMonitoring: React.FC = () => {
  const [activeTab, setActiveTab] = useState<string>('overview');
  const [engineStatus, setEngineStatus] = useState<AIEngineStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadEngineStatus();
    // 每30秒刷新一次狀態
    const interval = setInterval(loadEngineStatus, 30000);
    return () => clearInterval(interval);
  }, []);

  const loadEngineStatus = async () => {
    try {
      const status = await aiService.getEngineStatus();
      setEngineStatus(status);
      setError(null);
    } catch (err) {
      setError('無法載入AI引擎狀態');
      console.error('載入AI引擎狀態失敗:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleEngineToggle = async () => {
    if (!engineStatus) return;

    try {
      setLoading(true);
      if (engineStatus.isRunning) {
        await aiService.stopEngine();
        toast.success('AI檢測引擎已停止');
      } else {
        await aiService.startEngine();
        toast.success('AI檢測引擎已啟動');
      }
      await loadEngineStatus();
    } catch (err) {
      toast.error('引擎操作失敗');
      console.error('引擎操作失敗:', err);
    } finally {
      setLoading(false);
    }
  };

  const renderTabContent = () => {
    switch (activeTab) {
      case 'overview':
        return (
          <>
            <Row className="mb-4">
              <Col>
                <AIStats />
              </Col>
            </Row>
            <Row>
              <Col>
                <LiveAlerts />
              </Col>
            </Row>
          </>
        );
      case 'detections':
        return <DetectionHistory />;
      case 'cameras':
        return <CameraManagement />;
      default:
        return null;
    }
  };

  if (loading && !engineStatus) {
    return (
      <Container fluid className="py-4">
        <div className="text-center">
          <Spinner animation="border" role="status">
            <span className="visually-hidden">載入中...</span>
          </Spinner>
        </div>
      </Container>
    );
  }

  return (
    <Container fluid className="py-4">
      {/* 頁面標題和引擎控制 */}
      <Row className="mb-4">
        <Col>
          <div className="d-flex justify-content-between align-items-center">
            <div>
              <h2 className="mb-1">
                <i className="fas fa-shield-alt me-2"></i>
                AI安全監控
              </h2>
              <p className="text-muted mb-0">智能視頻監控與異常檢測系統</p>
            </div>
            <div className="d-flex align-items-center gap-3">
              {engineStatus && (
                <div className="d-flex align-items-center">
                  <Badge 
                    bg={engineStatus.isRunning ? 'success' : 'secondary'}
                    className="me-2"
                  >
                    {engineStatus.isRunning ? '運行中' : '已停止'}
                  </Badge>
                  {engineStatus.isRunning && (
                    <small className="text-muted">
                      運行時間: {Math.floor(engineStatus.uptime / 3600)}小時
                    </small>
                  )}
                </div>
              )}
              <Button
                variant={engineStatus?.isRunning ? 'danger' : 'success'}
                onClick={handleEngineToggle}
                disabled={loading}
              >
                {loading ? (
                  <Spinner animation="border" size="sm" className="me-2" />
                ) : (
                  <i className={`fas fa-${engineStatus?.isRunning ? 'stop' : 'play'} me-2`}></i>
                )}
                {engineStatus?.isRunning ? '停止引擎' : '啟動引擎'}
              </Button>
            </div>
          </div>
        </Col>
      </Row>

      {/* 錯誤提示 */}
      {error && (
        <Row className="mb-4">
          <Col>
            <Alert variant="danger" dismissible onClose={() => setError(null)}>
              <i className="fas fa-exclamation-triangle me-2"></i>
              {error}
            </Alert>
          </Col>
        </Row>
      )}

      {/* 標籤導航 */}
      <Row className="mb-4">
        <Col>
          <Card>
            <Card.Header className="border-0 bg-transparent">
              <ul className="nav nav-tabs card-header-tabs">
                <li className="nav-item">
                  <button
                    className={`nav-link ${activeTab === 'overview' ? 'active' : ''}`}
                    onClick={() => setActiveTab('overview')}
                  >
                    <i className="fas fa-tachometer-alt me-2"></i>
                    總覽
                  </button>
                </li>
                <li className="nav-item">
                  <button
                    className={`nav-link ${activeTab === 'detections' ? 'active' : ''}`}
                    onClick={() => setActiveTab('detections')}
                  >
                    <i className="fas fa-search me-2"></i>
                    檢測記錄
                  </button>
                </li>
                <li className="nav-item">
                  <button
                    className={`nav-link ${activeTab === 'cameras' ? 'active' : ''}`}
                    onClick={() => setActiveTab('cameras')}
                  >
                    <i className="fas fa-video me-2"></i>
                    攝像頭管理
                  </button>
                </li>
              </ul>
            </Card.Header>
            <Card.Body>
              {renderTabContent()}
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </Container>
  );
};

export default AIMonitoring;